import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { db, functions } from "../firebase";
import { collection, orderBy, query, where, onSnapshot } from "firebase/firestore";
import Masonry from "react-masonry-css";
import Header from "../components/Header";
import ProfileIcon from "../components/ProfileIcon";
import Button from "../components/Button";
import Icon from "../components/Icon";
import { Filter, Check } from 'lucide-react';
import NoteCard from "../components/NoteCard";
import { useAdminMode } from "../context/AdminModeContext";
import { httpsCallable } from "firebase/functions";
import { useCurrentMemorial } from "../context/CurrentMemorialContext";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import PhotoInfoOverlay from "../components/PhotoInfoOverlay";
import { formatDate } from "../utils/textHelpers";
import { useMemorial } from "../hooks/useMemorial";

export default function MemorialNotes() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [sortOrder, setSortOrder] = useState("desc");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const { adminMode } = useAdminMode();
  const { setCurrent } = useCurrentMemorial();
  const [isSelecting, setIsSelecting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const swiperRef = useRef(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showInfoOverlay, setShowInfoOverlay] = useState(true);
  const infoTimeoutRef = useRef(null);

  const { memorial, isOwner } = useMemorial(slug);

  // Keep global context in sync so Navbar always has the correct slug
  useEffect(() => {
    if (memorial?.id) setCurrent({ slug, memorialId: memorial.id });
  }, [memorial?.id, slug, setCurrent]);

  useEffect(() => () => { if (infoTimeoutRef.current) clearTimeout(infoTimeoutRef.current); }, []);

  const openNoteViewer = (note) => {
    if (adminMode && isOwner && isSelecting) {
      toggleSelect(note.id);
      return;
    }
    const idx = notes.findIndex(n => n.id === note.id);
    setSelectedIndex(idx !== -1 ? idx : 0);
    setSelectedNote(note);
    setShowInfoOverlay(true);
    infoTimeoutRef.current = setTimeout(() => setShowInfoOverlay(false), 3000);
  };

  const closeNoteViewer = () => {
    setSelectedNote(null);
    setSelectedIndex(null);
    if (infoTimeoutRef.current) clearTimeout(infoTimeoutRef.current);
  };

  const handleViewerClick = () => {
    if (infoTimeoutRef.current) clearTimeout(infoTimeoutRef.current);
    setShowInfoOverlay(prev => !prev);
    if (!showInfoOverlay) {
      infoTimeoutRef.current = setTimeout(() => setShowInfoOverlay(false), 3000);
    }
  };

  useEffect(() => {
    if (selectedNote && swiperRef.current && selectedIndex !== null) {
      // Swiper might need a tick to init
      // swiperRef.current.swiper.slideTo(selectedIndex, 0);
    }
  }, [selectedNote, selectedIndex]);


  useEffect(() => {
    if (!memorial?.id) return;
    const qNotes = query(
      collection(db, "memorials", memorial.id, "memories"),
      where("type", "==", "note"),
      orderBy("createdAt", sortOrder)
    );
    const unsub = onSnapshot(qNotes, (snapshot) => {
      setNotes(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsub();
  }, [memorial?.id, sortOrder]);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const deleteSelected = async () => {
    if (!memorial?.id || selectedIds.size === 0) return;
    const call = httpsCallable(functions, 'deleteMemories');
    await call({ memorialId: memorial.id, memoryIds: Array.from(selectedIds) });
    setNotes(prev => prev.filter(n => !selectedIds.has(n.id)));
    setSelectedIds(new Set());
    setIsSelecting(false);
    setDeleteConfirm(false);
  };

  const clearSelection = () => { setSelectedIds(new Set()); setIsSelecting(false); };

  // Do not block render on loading; show the page shell and let notes load when ready

  // Mirror the photo gallery's masonry breakpoints exactly so the two
  // gallery pages share the same rhythm of columns across viewports.
  const breakpointColumnsObj = { default: 3, 1024: 2, 768: 2 };

  const openViewerAt = (note) => openNoteViewer(note);

  return (
      <div className="min-h-screen bg-[var(--color-bg)] text-Accent-Minde overflow-x-hidden">
        <div className="fixed top-0 left-0 right-0 z-50">
        <Header title="Memory notes">
          <div className="flex items-center gap-3">
            <div className="relative">
              <button onClick={() => setDropdownOpen(prev => !prev)} className="h-11 w-11 rounded-full bg-[#44231E] flex items-center justify-center focus:outline-none">
                  <Filter size={22} color="#EFEBE0" strokeWidth={2} />
              </button>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 bg-white rounded-[16px] shadow-lg text-[#44231E] w-48 font-['Poppins'] overflow-hidden" style={{ zIndex: 60, border: '1px solid rgba(0,0,0,0.06)' }}>
                  <div onClick={() => { setSortOrder("desc"); setDropdownOpen(false); }} className={`px-5 py-3 hover:bg-[#F8F8F8] cursor-pointer flex justify-between items-center text-[16px] ${sortOrder === "desc" ? "bg-[#F8F8F8] font-medium" : "font-normal"}`}><span>Newest first</span>{sortOrder === "desc" && <Check size={15} color="#44231E" strokeWidth={2.5} />}</div>
                  <div onClick={() => { setSortOrder("asc"); setDropdownOpen(false); }} className={`px-5 py-3 hover:bg-[#F8F8F8] cursor-pointer flex justify-between items-center text-[16px] ${sortOrder === "asc" ? "bg-[#F8F8F8] font-medium" : "font-normal"}`}><span>Oldest first</span>{sortOrder === "asc" && <Check size={15} color="#44231E" strokeWidth={2.5} />}</div>
                </div>
              )}
            </div>
            <button onClick={() => setViewMode(prev => prev === 'list' ? 'grid' : 'list')} className="h-11 w-11 rounded-full bg-[#44231E] flex items-center justify-center focus:outline-none" title={viewMode === 'list' ? 'Grid view' : 'List view'}>
              {viewMode === 'list' ? (<Icon name="grid" size={22} color="#EFEBE0" />) : (<Icon name="list" size={22} color="#EFEBE0" />)}
            </button>
            <button onClick={() => navigate(`/m/${slug}/my-uploads`)} className="focus:outline-none"><ProfileIcon /></button>
          </div>
        </Header>
      </div>

      <div
        className="px-6 pb-24 overflow-y-auto"
        style={{
          paddingTop:
            adminMode && isOwner
              ? 160
              : 'max(76px, calc(68px + env(safe-area-inset-top, 0px)))',
        }}
      >
        {adminMode && isOwner && (
          <div style={{ position: 'fixed', top: 72, left: 16, right: 16, zIndex: 40, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: 10, borderRadius: 30, background: 'rgba(255,255,255,0.35)', border: '1px solid rgba(0,0,0,0.06)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
            <div>
              {isSelecting ? (
                <Button onClick={clearSelection}>Exit selection</Button>
              ) : (
                <Button onClick={() => setIsSelecting(true)}>Select</Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {deleteConfirm ? (
                <>
                  <span style={{ fontSize: 13, fontFamily: 'Poppins', color: '#44231E' }}>
                    Delete {selectedIds.size}?
                  </span>
                  <Button onClick={deleteSelected}>Yes</Button>
                  <Button onClick={() => setDeleteConfirm(false)}>No</Button>
                </>
              ) : (
                <Button variant="danger" onClick={() => setDeleteConfirm(true)} disabled={selectedIds.size === 0}>
                  {`Delete ${selectedIds.size} selected`}
                </Button>
              )}
            </div>
          </div>
        )}
        {viewMode === 'grid' ? (
          <Masonry
            breakpointCols={breakpointColumnsObj}
            className="flex"
            columnClassName="flex flex-col"
          >
            {notes.map((note, index) => (
              <NoteCard
                key={note.id || index}
                note={note}
                date={formatDate(note.createdAt)}
                index={index}
                maxWords={16}
                onClick={() => openViewerAt(note)}
                isSelected={adminMode && isOwner && selectedIds.has(note.id)}
              />
            ))}
          </Masonry>
        ) : (
          <div className="flex flex-col">
            {notes.map((note, index) => (
              <NoteCard
                key={note.id || index}
                note={note}
                date={formatDate(note.createdAt)}
                index={index}
                maxWords={55}
                onClick={() => openViewerAt(note)}
                isSelected={adminMode && isOwner && selectedIds.has(note.id)}
                plain
              />
            ))}
          </div>
        )}
        {notes.length === 0 && (
          <p className="text-center mt-10 text-[14px] text-[#6A5A45] font-['Poppins']">
            No memory notes yet.
          </p>
        )}
      </div>

      <AnimatePresence>
        {selectedNote && (
          <motion.div 
            className="fixed inset-0 bg-black bg-opacity-80 z-[70]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {/* Close Button */}
            <div className="absolute top-4 right-6 z-[80]">
              <button
                onClick={closeNoteViewer}
                style={{
                  width: 44,
                  height: 44,
                  padding: 10,
                  background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.35)',
                  borderRadius: 100,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(10px)',
                  WebkitBackdropFilter: 'blur(10px)',
                }}
              >
                <Icon name="xWhite" size={24} color="#FFFFFF" />
              </button>
            </div>

            {/* Swiper for Notes */}
            <div className="w-full h-full flex items-center justify-center" onClick={handleViewerClick}>
              <Swiper
                ref={swiperRef}
                modules={[Pagination]}
                style={{ 
                  '--swiper-pagination-color': '#fff', 
                  '--swiper-pagination-bullet-inactive-color': '#aaa', 
                  '--swiper-pagination-bullet-inactive-opacity': '1', 
                  '--swiper-pagination-bullet-size': '6px', 
                  '--swiper-pagination-bottom': '46px',
                  width: '100%',
                  height: '100%' 
                }}
                spaceBetween={20}
                slidesPerView={1}
                pagination={{ clickable: true }}
                initialSlide={selectedIndex}
                onSlideChange={(swiper) => {
                  setSelectedIndex(swiper.realIndex);
                  setSelectedNote(notes[swiper.realIndex]);
                  setShowInfoOverlay(true);
                  if (infoTimeoutRef.current) clearTimeout(infoTimeoutRef.current);
                  infoTimeoutRef.current = setTimeout(() => setShowInfoOverlay(false), 3000);
                }}
                className="h-full"
              >
                {notes.map((note, index) => (
                  <SwiperSlide key={note.id} className="flex items-center justify-center px-4">
                  <div className="flex items-center justify-center w-full h-full">
                    <div 
                      className="w-full bg-white rounded-[20px] shadow-lg overflow-hidden flex flex-col max-h-[calc(100vh-170px)]" 
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="overflow-y-auto p-8">
                            {String(note.text || '').split(/\n+/).filter(Boolean).map((paragraph, idx, arr) => (
                                <p 
                                    key={idx} 
                                    className="text-[16px] font-['Poppins'] text-[#000000] font-normal text-left" 
                                    style={{ lineHeight: 1.6, marginBottom: idx === arr.length - 1 ? 0 : 16 }}
                                >
                                    {paragraph}
                                </p>
                            ))}
                        </div>
                      </div>
                    </div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>

            {/* Info Overlay */}
            <div className={`absolute bottom-10 left-0 right-0 z-[75] px-6 transition-opacity duration-300 ease-in-out flex justify-center ${showInfoOverlay ? 'opacity-100' : 'opacity-0'}`}>
              {selectedNote && (
                <PhotoInfoOverlay
                  author={(selectedNote.authorName === 'Anonym' ? 'Anonymous' : (selectedNote.authorName || selectedNote.author)) || 'Anonymous'}
                  date={formatDate(selectedNote.createdAt)}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


